<template>
  <transition name="preika-command-fade">
    <div
      v-if="open"
      class="preika-command-overlay"
      role="dialog"
      aria-modal="true"
      aria-label="PREIKA横断検索"
      @mousedown.self="closePalette"
    >
      <section class="preika-command-palette">
        <header class="preika-command-search">
          <CIcon name="cil-magnifying-glass"/>
          <input
            ref="queryInput"
            v-model="query"
            type="search"
            autocomplete="off"
            placeholder="画面・ユーザー・プロジェクト・取引先を検索"
            @keydown.down.prevent="move(1)"
            @keydown.up.prevent="move(-1)"
            @keydown.enter.prevent="activateSelected"
            @keydown.esc.prevent="closePalette"
          >
          <kbd>Esc</kbd>
        </header>

        <div class="preika-command-body">
          <div v-if="loading" class="preika-command-loading">
            <span class="preika-command-spinner"></span>
            検索しています
          </div>

          <template v-if="staticResults.length">
            <p class="preika-command-group-title">画面・操作</p>
            <button
              v-for="item in staticResults"
              :key="'static-' + item.key"
              type="button"
              class="preika-command-item"
              :class="{ active: selectedItem && selectedItem.identity === 'static-' + item.key }"
              @mouseenter="selectByIdentity('static-' + item.key)"
              @click="go(item)"
            >
              <span class="preika-command-item__icon"><CIcon :name="item.icon || 'cil-apps'"/></span>
              <span class="preika-command-item__copy">
                <strong>{{ item.title }}</strong>
                <small>{{ item.subtitle }}</small>
              </span>
              <span class="preika-command-item__type">画面</span>
            </button>
          </template>

          <template v-if="dynamicResults.length">
            <p class="preika-command-group-title">データ</p>
            <button
              v-for="item in dynamicResults"
              :key="'dynamic-' + item.type + '-' + item.id"
              type="button"
              class="preika-command-item"
              :class="{ active: selectedItem && selectedItem.identity === dynamicIdentity(item) }"
              @mouseenter="selectByIdentity(dynamicIdentity(item))"
              @click="go(item)"
            >
              <span class="preika-command-item__icon"><CIcon :name="item.icon || 'cil-magnifying-glass'"/></span>
              <span class="preika-command-item__copy">
                <strong>{{ item.title }}</strong>
                <small>{{ item.subtitle }}</small>
              </span>
              <span class="preika-command-item__type">{{ dynamicTypeLabel(item.type) }}</span>
            </button>
          </template>

          <div v-if="!loading && !allItems.length" class="preika-command-empty">
            <span><CIcon name="cil-magnifying-glass"/></span>
            <strong>一致する項目がありません</strong>
            <p>名称、メールアドレス、プロジェクト名などで検索してください。</p>
          </div>
        </div>

        <footer class="preika-command-footer">
          <span><kbd>↑</kbd><kbd>↓</kbd> 移動</span>
          <span><kbd>Enter</kbd> 開く</span>
          <span class="preika-command-footer__scope">{{ scopeLabel }}</span>
        </footer>
      </section>
    </div>
  </transition>
</template>

<script>
import axios from 'axios'
import { commandPaletteItems, filterCommandPaletteItems } from '../utils/commandPalette'
import { isManagementAuthority } from '../utils/managementInsights'

export default {
  name: 'GlobalCommandPalette',

  props: {
    authority: {
      type: String,
      default: ''
    }
  },

  data () {
    return {
      open: false,
      query: '',
      dynamicResults: [],
      loading: false,
      selectedIndex: 0,
      timerId: null,
      requestId: 0
    }
  },

  computed: {
    commands () {
      return commandPaletteItems(this.authority)
    },

    staticResults () {
      return filterCommandPaletteItems(this.commands, this.query)
    },

    allItems () {
      const statics = this.staticResults.map(item => ({ ...item, identity: `static-${item.key}` }))
      const dynamics = this.dynamicResults.map(item => ({ ...item, identity: this.dynamicIdentity(item) }))
      return [...statics, ...dynamics]
    },

    selectedItem () {
      return this.allItems[this.selectedIndex] || null
    },

    scopeLabel () {
      return isManagementAuthority(this.authority)
        ? '管理データを含む横断検索'
        : '所属プロジェクト内を検索'
    }
  },

  watch: {
    query () {
      this.selectedIndex = 0
      this.scheduleDynamicSearch()
    },

    allItems (items) {
      if (!items.length) this.selectedIndex = 0
      else if (this.selectedIndex >= items.length) this.selectedIndex = items.length - 1
    }
  },

  mounted () {
    window.addEventListener('keydown', this.handleGlobalKeydown)
    window.addEventListener('preika:command-palette-open', this.openPalette)
  },

  beforeDestroy () {
    window.removeEventListener('keydown', this.handleGlobalKeydown)
    window.removeEventListener('preika:command-palette-open', this.openPalette)
    if (this.timerId) window.clearTimeout(this.timerId)
  },

  methods: {
    handleGlobalKeydown (event) {
      const target = event.target
      const editable = target && (
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.tagName === 'SELECT' ||
        target.isContentEditable
      )

      if ((event.ctrlKey || event.metaKey) && String(event.key).toLowerCase() === 'k') {
        event.preventDefault()
        this.openPalette()
        return
      }

      if (event.key === 'Escape' && this.open && !editable) {
        this.closePalette()
      }
    },

    openPalette () {
      this.open = true
      this.selectedIndex = 0
      this.$nextTick(() => {
        if (this.$refs.queryInput) this.$refs.queryInput.focus()
      })
    },

    closePalette () {
      this.open = false
      this.query = ''
      this.dynamicResults = []
      this.loading = false
      this.requestId += 1
    },

    scheduleDynamicSearch () {
      if (this.timerId) window.clearTimeout(this.timerId)
      const value = this.query.trim()
      if (!value) {
        this.dynamicResults = []
        this.loading = false
        return
      }
      this.timerId = window.setTimeout(() => this.fetchDynamicResults(value), 240)
    },

    fetchDynamicResults (value) {
      const currentRequest = ++this.requestId
      this.loading = true
      return axios
        .get(process.env.VUE_APP_API_ORIGIN + '/search/global', {
          params: { q: value, limit: 15 }
        })
        .then(response => {
          if (currentRequest !== this.requestId) return
          const data = response.data || {}
          this.dynamicResults = Array.isArray(data.items) ? data.items : []
        })
        .catch(error => {
          if (currentRequest !== this.requestId) return
          this.dynamicResults = []
          console.error('横断検索に失敗しました:', error)
        })
        .finally(() => {
          if (currentRequest === this.requestId) this.loading = false
        })
    },

    move (direction) {
      if (!this.allItems.length) return
      const length = this.allItems.length
      this.selectedIndex = (this.selectedIndex + direction + length) % length
    },

    activateSelected () {
      if (this.selectedItem) this.go(this.selectedItem)
    },

    selectByIdentity (identity) {
      const index = this.allItems.findIndex(item => item.identity === identity)
      if (index >= 0) this.selectedIndex = index
    },

    dynamicIdentity (item) {
      return `dynamic-${item.type}-${item.id}`
    },

    dynamicTypeLabel (type) {
      return ({ USER: 'ユーザー', PROJECT: 'PJ', COMPANY: '取引先' })[type] || 'データ'
    },

    go (item) {
      if (!item || !item.route) return
      const route = item.route
      this.closePalette()
      if (this.$route.fullPath !== route) {
        this.$router.push(route).catch(() => {})
      }
    }
  }
}
</script>
